// import { Route, Routes, Switch } from "react-router-dom";
import "./App.css";
import { useEffect, lazy, Suspense } from "react";
import CustomerRoutes from "./Routers/CustomerRoutes";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./Routers/ProtectedRoute";
import RouteFallback from "./components/RouteFallback";
import ScrollToTop from "./components/ScrollToTop";
import api from "./config/api";
import { currentPageTitle, formatTitle } from "./utils/pageTitle";

// The admin panel — order tables, charts, the settings screen — is a large slice
// of the bundle that a customer never routes to. Loading it on demand keeps it
// out of the storefront's initial download.
const AdminPannel = lazy(() => import("./Admin/AdminPannel"));

// Auto sign-out after this much inactivity (customer + admin).
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Point the browser-tab icon at the given URL (creating the <link> if needed).
const setFavicon = (href) => {
  if (!href) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
};

const App = () => {
  const { auth } = useSelector((store) => store);

  // Set the browser tab title from the configured brand name (white-label).
  useEffect(() => {
    api
      .get("/api/settings")
      .then(({ data }) => {
        if (data?.brandName) {
          // Cache for the pre-React loading splash (white-label, no hardcoding).
          try {
            localStorage.setItem("brandName", data.brandName);
          } catch (e) {}
          // A page that set its own title ("Royal look — Rangrez") keeps it.
          document.title = currentPageTitle()
            ? formatTitle(currentPageTitle())
            : data.brandName;
        }
        // Cache the logo for the pre-React loading splash (index.html).
        try {
          if (data?.logoUrl) localStorage.setItem("logoUrl", data.logoUrl);
          else localStorage.removeItem("logoUrl");
        } catch (e) {}
        // Apply + cache the admin favicon (cache lets the pre-React load set it
        // immediately, avoiding a flash of the default icon).
        const fav = data?.faviconUrl || "";
        try {
          if (fav) localStorage.setItem("faviconUrl", fav);
          else localStorage.removeItem("faviconUrl");
        } catch (e) {}
        setFavicon(fav);
      })
      .catch(() => {});
  }, []);

  // Inactivity session timeout: while signed in, sign the user out after
  // IDLE_TIMEOUT_MS with no interaction. Any activity resets the timer.
  useEffect(() => {
    if (!auth?.user && !localStorage.getItem("jwt")) return undefined;
    let timer;
    const signOutIdle = () => {
      if (!localStorage.getItem("jwt")) return;
      localStorage.removeItem("jwt");
      window.location.replace("/?session=1");
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(signOutIdle, IDLE_TIMEOUT_MS);
    };
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [auth?.user]);

  return (
    // Mounted here rather than inside CustomerRoutes so the admin panel gets it
    // too — its tables and forms are long and were left mid-scroll on every
    // navigation.
    <>
      <ScrollToTop />
      <Routes>
      {/* Admin routes are always registered and gated by the token (available
          synchronously on reload) — so refreshing an /admin page renders the
          panel immediately instead of briefly falling through to the 404 page
          while auth.user is still loading. AdminPannel enforces the ADMIN role. */}
      <Route element={<ProtectedRoute />}>
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AdminPannel />
            </Suspense>
          }
        />
      </Route>
      {auth?.user?.role === "ADMIN" && (
        <Route path="/" element={<Navigate to="/admin" />} />
      )}
      <Route path="/*" element={<CustomerRoutes />} />
      </Routes>
    </>
  );
};

export default App;
